// Foundations
import { AfterViewInit, Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { MatTab, MatTabGroup, MatTabHeader } from '@angular/material/tabs';
import { Title } from '@angular/platform-browser';

// rxjs
import { filter, finalize, map, take } from 'rxjs/operators';

// Components
import { BeginPageComponent } from './begin-page/begin-page.component';
import { ContactDetailsPageComponent } from './contact-details-page/contact-details-page.component';
import { OtherContactsPageComponent } from './other-contacts-page/other-contacts-page.component';
import { CampLocationsPageComponent } from './camp-locations-page/camp-locations-page.component';

// Services
import { AnalyticsService } from '../../services/analytics.service';
import { DialogService } from '../../services/dialog.service';
import {
  AddCamperCampingQuizAnswerGQL,
  GetCamperCampingQuizAnswerGQL
} from '../../services/graphql.service';
import { AuthService } from '../../auth/auth.service';
import { LoadingIndicatorService } from '../../services/loading-indicator.service';

// Models
import { CanDeactivateComponent } from 'src/app/models/can-deactivate-component.model';

@Component({
  selector: 'app-my-profile-page',
  templateUrl: './my-profile-page.component.html',
  styleUrls: ['./my-profile-page.component.scss'],
})
export class MyProfilePageComponent
  implements OnInit, AfterViewInit, CanDeactivateComponent
{
  @HostBinding('class') class = 'page-config';

  @ViewChild('beginForm', { static: false }) beginForm: BeginPageComponent;
  @ViewChild('cdPage', { static: false }) cdPage: ContactDetailsPageComponent;
  @ViewChild('fmPage', { static: false }) fmPage: OtherContactsPageComponent;
  @ViewChild('clPage', { static: false }) clPage: CampLocationsPageComponent;
  @ViewChild('tabs', { static: false }) tabs: MatTabGroup;

  currIndex = 0;
  isFirstTime = false;
  displayTabs = false;

  constructor(
    private dialogService: DialogService,
    private _analytics: AnalyticsService,
    private getQuizAnwserQuery: GetCamperCampingQuizAnswerGQL,
    private addQuizAnswerMutation: AddCamperCampingQuizAnswerGQL,
    private authService: AuthService,
    private loadingIndicatorService: LoadingIndicatorService,
    private titleService: Title
  ) {
    this.titleService.setTitle('Client Portal - My Profile');
  }

  canDeactivate() {
    if (
      (this.cdPage?.cdForm && !this.cdPage.cdForm.getForm().pristine) ||
      (this.cdPage?.haForm && !this.cdPage.haForm.getForm().pristine) ||
      (this.fmPage?.newContactForm &&
        !this.fmPage.newContactForm.getForm().pristine) ||
      (this.fmPage?.editContactForm &&
        !this.fmPage.editContactForm.getForm().pristine) ||
      (this.clPage?.editPreferredLocation &&
        !this.clPage.editPreferredLocation.selectedId.pristine)
    ) {
      return this.dialogService.confirmDiscard();
    }
    return true;
  }

  // Life cycles
  // ------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._analytics.logScreen('myProfilePage');
    this.checkFirstTime();
  }

  ngAfterViewInit(): void {
    this.tabs._handleClick = this.interceptTabChange.bind(this);
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  navigateToTabIndex(tabIndex: number) {
    this.currIndex = tabIndex;
  }

  onSelectedIndexChanged(args) {
    this.currIndex = args.newIndex;
  }

  onBegin() {
    this.submitCamperQuiz();
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private interceptTabChange(
    tab: MatTab,
    tabHeader: MatTabHeader,
    idx: number
  ) {
    if (this.currIndex === idx) {
      return;
    }
    if (
      (this.cdPage?.cdForm && !this.cdPage.cdForm.getForm().pristine) ||
      (this.cdPage?.haForm && !this.cdPage.haForm.getForm().pristine) ||
      (this.fmPage?.newContactForm &&
        !this.fmPage.newContactForm.getForm().pristine) ||
      (this.fmPage?.editContactForm &&
        !this.fmPage.editContactForm.getForm().pristine) ||
      (this.clPage?.editPreferredLocation &&
        !this.clPage.editPreferredLocation.selectedId.pristine)
    ) {
      this.dialogService.confirmDiscard().subscribe((isDiscard) => {
        if (isDiscard) {
          this.cdPage.resetFormToDefault();
          this.fmPage.resetFormToDefault();
          this.clPage.editPreferredLocation?.resetFormToDefault();
          return MatTabGroup.prototype._handleClick.apply(this.tabs, [
            tab,
            tabHeader,
            idx,
          ]);
        }
      });
    } else {
      MatTabGroup.prototype._handleClick.apply(this.tabs, [
        tab,
        tabHeader,
        idx,
      ]);
    }
  }

  private checkFirstTime(): void {
    this.loadingIndicatorService.show();
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.getQuizAnwserQuery
          .fetch({ accountId })
          .pipe(
            map((res) => res.data.getCamperCampingQuizAnswer.choices),
            finalize(() => this.loadingIndicatorService.hide())
          )
          .subscribe((choices) => {
            if (choices.length === 0) {
              this.isFirstTime = true;
            } else {
              this.displayTabs = true;
            }
          });
      });
  }

  private submitCamperQuiz() {
    this.loadingIndicatorService.show();
    this.authService.accountId$
      .pipe(
        filter((accountId) => !!accountId),
        take(1)
      )
      .subscribe((accountId) => {
        this.addQuizAnswerMutation
          .mutate({
            accountId,
            answer: {
              choices: this.beginForm.selectedOptions,
            },
          })
          .pipe(finalize(() => this.loadingIndicatorService.hide()))
          .subscribe((res) => {
            if (res.data) {
              this._analytics.logButtonTap('myProfile_begin');
              this.displayTabs = true;
            }
          });
      });
  }
}
